/**
 * QuoteOpsStatusRibbon — status tiles matching Quote Ops mock.
 */

import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  QUOTE_OPS_BUCKETS,
  computeQuoteOperationalSummary,
  getQuoteOperationalLabel,
} from "../services/quoteOperationalStatus.js";

const Ribbon = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(118px, 1fr))",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const Tile = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected" && prop !== "accent",
})(({ theme, selected, accent }) => ({
  cursor: "pointer",
  borderRadius: 12,
  padding: theme.spacing(1.25, 1.5),
  border: `1px solid ${selected ? accent : theme.palette.divider}`,
  backgroundColor: selected ? `${accent}14` : theme.palette.background.paper,
  transition: "border-color 120ms ease, background-color 120ms ease",
  "&:hover": {
    borderColor: accent,
    backgroundColor: `${accent}0F`,
  },
}));

const TILE_ACCENTS = {
  draft: "#1976d2",
  ready_to_send: "#2e7d32",
  sent: "#5e35b1",
  viewed: "#607d8b",
  negotiating: "#00838f",
  deposit_requested: "#ef6c00",
  deposit_received: "#1b5e20",
  accepted: "#00695c",
  expired: "#c62828",
  rejected: "#ad1457",
  ordered: "#311b92",
};

const RIBBON_BUCKETS = QUOTE_OPS_BUCKETS.filter((b) => b !== "ordered");

const QuoteOpsStatusRibbon = ({
  quotes = [],
  activeBucket = null,
  onBucketClick,
}) => {
  const summary = useMemo(
    () => computeQuoteOperationalSummary(quotes),
    [quotes]
  );

  return (
    <Ribbon data-testid="quote-ops-status-ribbon">
      <Tile
        selected={activeBucket == null}
        accent="#455a64"
        onClick={onBucketClick ? () => onBucketClick(null) : undefined}
      >
        <Typography
          variant="caption"
          sx={{ color: "#455a64", fontWeight: 700, letterSpacing: 0.3 }}
        >
          All
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {quotes.length}
        </Typography>
      </Tile>
      {RIBBON_BUCKETS.map((bucket) => {
        const count = summary[bucket] || 0;
        const selected = activeBucket === bucket;
        const accent = TILE_ACCENTS[bucket] || "#757575";
        return (
          <Tile
            key={bucket}
            selected={selected}
            accent={accent}
            onClick={
              onBucketClick
                ? () => onBucketClick(selected ? null : bucket)
                : undefined
            }
          >
            <Typography
              variant="caption"
              sx={{ color: accent, fontWeight: 700, displaySpacing: 0.3 }}
            >
              {getQuoteOperationalLabel(bucket)}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {count}
            </Typography>
          </Tile>
        );
      })}
    </Ribbon>
  );
};

export default QuoteOpsStatusRibbon;
